import { Navigate } from 'react-router'
// Reaches into another route module, which is the pattern `FormError` was just
// moved out of — but `SessionUnavailable` is a SCREEN and renders an
// `AuthCard`, so moving it to `src/auth/` would drag a presentational component
// into the domain layer to fix an import. `RequireAuth` already imports it this
// way and did before this work. Left as it is, deliberately, and tracked rather
// than half-solved. web-architect raised it as pre-existing.
import { SessionUnavailable } from '../auth/SessionUnavailable'
import { useReturnTo } from '../../auth/returnTo'
import { useAuth } from '../../auth/useAuth'
import { SecurityInvite } from './SecurityInvite'

/**
 * First run, and the only thing on it is an optional security invitation.
 *
 * **Not inside `RedirectWhenSignedIn`.** That guard sends every signed-in user
 * to their destination, which is exactly the population this screen exists for
 * — it would bounce this route away the instant it rendered. Held out of the
 * BARE group for the same reason `/verify/:token` is.
 *
 * **The exit is unconditional.** `settled` navigates away whether the PATCH
 * succeeded or not, and every path through `SecurityInvite` reaches it. A
 * first-run screen that can strand someone is worse than no first-run screen,
 * and this codebase has already paid for that lesson once on `/mfa`.
 */
export function Welcome() {
  const auth = useAuth()
  /**
   * Where they were actually going.
   *
   * `RequireAuth` remembers it before sending anyone here, so a deep link
   * survives first run rather than being replaced by Overview. Consumed only
   * once first run is finished — the hook's own rule, and the reason it takes
   * the condition rather than being called after an early return.
   */
  // Narrowed through `status`, because `profile` exists only on the signedIn
  // arm — and the hook must run unconditionally, above every early return.
  const finished = auth.status === 'signedIn' && auth.profile.is_onboarded === true
  const destination = useReturnTo(finished)

  // Before there is an answer, render nothing rather than bouncing — the rule
  // `RequireAuth` states, for the same reason.
  if (auth.status === 'unknown' || auth.status === 'bootstrapping') return null
  // Authenticated with no app session. In place, not a bounce to /signin: the
  // user HAS a live Firebase session, and the sign-in form has nothing to offer
  // them. `RequireAuth` gives the same answer to the same situation.
  if (auth.status === 'sessionUnavailable') return <SessionUnavailable />
  if (auth.status !== 'signedIn') return <Navigate to="/signin" replace />
  // No local "leaving" flag. One existed and it could not work: the navigation
  // unmounts the component holding it, so a guard that sent the user back found
  // a freshly mounted screen with the flag reset. The flag lives in auth state,
  // which is the thing the guard actually reads.
  if (auth.profile.is_onboarded === true) return <Navigate to={destination} replace />

  return (
    <SecurityInvite onSettled={() => void auth.markOnboarded()} />
  )
}
